import React, { useState } from 'react';
import { cadastroService } from '../../services/api';
import DraggableWindow from './DraggableWindow';

// Campos editáveis enviados ao salvar (devem bater com a allowlist do proxy)
const CAMPOS = [
  'NOME', 'CPF', 'IDENTIDADE', 'NASCIMENTO', 'SEXO', 'FONE',
  'C20_FONECO', 'EMAIL', 'CEP', 'ENDE', 'NRENDE', 'COMPL', 'BAIR', 'CIDA',
  'ESTA', 'PONTOREF', 'RECEBEOFERTA',
  'SNGPC_TIPODOC', 'SNGPC_NUMDOC', 'SNGPC_ORGAO', 'SNGPC_UF', 'NRCRM',
];

// A API pode devolver NASCIMENTO como AAAAMMDD, data ISO ou DD/MM/AAAA.
// O input date sempre precisa receber AAAA-MM-DD.
const nascParaInput = (v) => {
  const valor = String(v ?? '').trim();
  if (!valor) return '';

  const iso = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const br = valor.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;

  const digitos = valor.replace(/\D/g, '');
  if (digitos.length !== 8) return '';

  // Formato principal do pdvcliente: AAAAMMDD.
  if (/^(19|20)\d{6}$/.test(digitos)) {
    return `${digitos.slice(0, 4)}-${digitos.slice(4, 6)}-${digitos.slice(6, 8)}`;
  }

  // Compatibilidade com registros antigos em DDMMAAAA.
  if (/^(19|20)\d{2}$/.test(digitos.slice(4))) {
    return `${digitos.slice(4)}-${digitos.slice(2, 4)}-${digitos.slice(0, 2)}`;
  }

  return '';
};
const inputParaNasc = (v) => String(v ?? '').replace(/\D/g, '').slice(0, 8); // 'YYYY-MM-DD' -> 'YYYYMMDD'

const sexoParaInput = (v) => {
  const sexo = String(v ?? '').trim().toUpperCase();
  if (['M', 'MASCULINO', '1'].includes(sexo)) return 'M';
  if (['F', 'FEMININO', '2'].includes(sexo)) return 'F';
  return '';
};

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 };
const inputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

// Precisa ficar fora de WindowCadCliente. Se o componente for recriado a cada
// render, o React desmonta o input após cada tecla e o campo perde o foco.
const FormField = ({ name, label, value, onChange, width = 1, type = 'text', readOnly = false }) => (
  <div style={{ flex: width, minWidth: 120 }}>
    <label style={labelStyle}>{label}</label>
    <input
      style={{ ...inputStyle, background: readOnly ? '#f1f5f9' : '#fff' }}
      type={type}
      value={value ?? ''}
      readOnly={readOnly}
      onChange={(e) => onChange(name, e.target.value)}
    />
  </div>
);

const WindowCadCliente = ({ onClose, onMinimize, isMinimized, zIndex, onFocus }) => {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [form, setForm] = useState(null);     // cliente carregado p/ edição
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buscar = async () => {
    setErro(''); setSucesso(''); setResultados([]); setForm(null);
    if (!termo.trim()) { setErro('Informe código, CPF ou nome.'); return; }
    setBuscando(true);
    try {
      const rows = await cadastroService.buscarCliente(termo);
      if (!rows || rows.length === 0) setErro('Nenhum cliente encontrado.');
      else if (rows.length === 1) carregar(rows[0]);
      else setResultados(rows);
    } catch (e) {
      setErro(e.message || 'Erro ao buscar.');
    } finally {
      setBuscando(false);
    }
  };

  const carregar = (c) => {
    setResultados([]);
    setForm({ ...c });
  };

  const salvar = async () => {
    if (!form?.CODIGO) return;
    setErro(''); setSucesso('');
    const campos = {};
    CAMPOS.forEach((k) => { campos[k] = form[k] ?? ''; });
    setSalvando(true);
    try {
      await cadastroService.salvarCliente(form.CODIGO, campos);
      setSucesso(`Cliente ${form.CODIGO} alterado com sucesso.`);
    } catch (e) {
      setErro(e.message || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <DraggableWindow title="Alteração de Cadastro — Cliente" icon="fa-user-pen"
      onClose={onClose} onMinimize={onMinimize} isMinimized={isMinimized}
      zIndex={zIndex} onFocus={onFocus} initialWidth={760}>
      <div style={{ padding: 16 }}>
        {/* Busca */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input style={{ ...inputStyle, flex: 1 }} value={termo} placeholder="Código, CPF ou nome do cliente..."
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} autoFocus />
          <button onClick={buscar} disabled={buscando}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#0078d7', color: '#fff', cursor: 'pointer' }}>
            {buscando ? '...' : 'Buscar'}
          </button>
        </div>

        {/* Lista de resultados (quando há vários) */}
        {resultados.length > 0 && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 14, maxHeight: 200, overflowY: 'auto' }}>
            {resultados.map((c) => (
              <div key={c.CODIGO} onClick={() => carregar(c)}
                style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 13 }}>
                <b>{c.CODIGO}</b> — {c.NOME} {c.CPF ? `· CPF ${c.CPF}` : ''}
              </div>
            ))}
          </div>
        )}

        {/* Formulário */}
        {form && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="CODIGO" label="Código" width={0.5} value={form.CODIGO} onChange={set} readOnly />
              <FormField name="NOME" label="Nome" width={2} value={form.NOME} onChange={set} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="CPF" label="CPF" value={form.CPF} onChange={set} />
              <FormField name="IDENTIDADE" label="Identidade (RG)" value={form.IDENTIDADE} onChange={set} />
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={labelStyle}>Nascimento</label>
                <input style={inputStyle} type="date"
                  value={nascParaInput(form?.NASCIMENTO)}
                  onChange={(e) => set('NASCIMENTO', inputParaNasc(e.target.value))} />
              </div>
              <div style={{ width: 90 }}>
                <label style={labelStyle}>Sexo</label>
                <select style={inputStyle} value={sexoParaInput(form.SEXO)} onChange={(e) => set('SEXO', e.target.value)}>
                  <option value="">-</option><option value="M">M</option><option value="F">F</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="FONE" label="Telefone" value={form.FONE} onChange={set} />
              <FormField name="C20_FONECO" label="Celular/Contato" value={form.C20_FONECO} onChange={set} />
            </div>
            <FormField name="EMAIL" label="E-mail" value={form.EMAIL} onChange={set} />
            <div style={{ display: 'flex', gap: 10, margin: '10px 0' }}>
              <FormField name="CEP" label="CEP" width={0.7} value={form.CEP} onChange={set} />
              <FormField name="ENDE" label="Endereço" width={2} value={form.ENDE} onChange={set} />
              <FormField name="NRENDE" label="Número" width={0.6} value={form.NRENDE} onChange={set} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="COMPL" label="Complemento" value={form.COMPL} onChange={set} />
              <FormField name="BAIR" label="Bairro" value={form.BAIR} onChange={set} />
              <FormField name="CIDA" label="Cidade" value={form.CIDA} onChange={set} />
              <FormField name="ESTA" label="UF" width={0.4} value={form.ESTA} onChange={set} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="PONTOREF" label="Ponto de referência" width={2} value={form.PONTOREF} onChange={set} />
            </div>

            {/* SNGPC */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', margin: '6px 0' }}>SNGPC / Prescritor</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <FormField name="SNGPC_TIPODOC" label="Tipo Doc" width={0.7} value={form.SNGPC_TIPODOC} onChange={set} />
              <FormField name="SNGPC_NUMDOC" label="Nº Doc" value={form.SNGPC_NUMDOC} onChange={set} />
              <FormField name="SNGPC_ORGAO" label="Órgão" width={0.6} value={form.SNGPC_ORGAO} onChange={set} />
              <FormField name="SNGPC_UF" label="UF Doc" width={0.4} value={form.SNGPC_UF} onChange={set} />
              <FormField name="NRCRM" label="CRM" width={0.7} value={form.NRCRM} onChange={set} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={String(form.RECEBEOFERTA).toUpperCase() === 'S'}
                  onChange={(e) => set('RECEBEOFERTA', e.target.checked ? 'S' : 'N')} /> Recebe ofertas
              </label>
            </div>

            <button onClick={salvar} disabled={salvando}
              style={{ width: '100%', padding: 12, border: 'none', borderRadius: 8, background: salvando ? '#94a3b8' : '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: salvando ? 'not-allowed' : 'pointer' }}>
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </>
        )}

        {erro && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13 }}><i className="fas fa-triangle-exclamation" /> {erro}</div>}
        {sucesso && <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13 }}><i className="fas fa-check-circle" /> {sucesso}</div>}
      </div>
    </DraggableWindow>
  );
};

export default WindowCadCliente;
